import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Paper,
} from "@mui/material";
import * as Mui from "@mui/material";
import {
  ArrowBack,
  Battery60,
  DateRange,
  MotionPhotosAuto,
  SocialDistance,
} from "@mui/icons-material";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { tableRowSize } from "services/constants";
import { themes } from "services/constants";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { Loader } from "components/Loader";
import {
  BatteryChargingFullRounded,
  BatteryFullRounded,
  Flag,
  AccessTime,
  LocationOn,
  Timer,
} from "@mui/icons-material";
import dayjs from "dayjs";

const tripHeader = [
  {
    name: "Date",
    key: "date",
    type: "date",
    icon: <DateRange fontSize="small" />,
  },
  {
    name: "Time",
    key: "time",
    type: "time",
    icon: <Timer fontSize="small" />,
    showBetweenOperator: false,
  },
  {
    name: "Latitude - Longitude",
    key: `latLong`,
    type: "number",
    icon: <AccessTime fontSize="small" />,
  },
  {
    name: "Motion Type",
    key: "motion_type",
    type: "autocomplete",
    icon: <MotionPhotosAuto fontSize="small" />,
  },
  {
    name: "Distance Covered (km)",
    key: "distance_cover",
    type: "number",
    icon: <SocialDistance fontSize="small" />,
    showBetweenOperator: true,
  },
  {
    name: "Battery Level",
    key: "batteryPercentage",
    type: "number",
    icon: <Battery60 fontSize="small" />,
    showBetweenOperator: true,
  },
];

const filteredTripHeader = tripHeader.flatMap((h) => {
  if (h.key === "latLong") {
    return [
      {
        name: "Latitude",
        key: "latitude",
        type: "number",
        icon: <AccessTime fontSize="small" />,
        showBetweenOperator: false,
      },
      {
        name: "Longitude",
        key: "longitude",
        type: "number",
        icon: <AccessTime fontSize="small" />,
        showBetweenOperator: false,
      },
    ];
  }
  if (h.key === "batteryPercentage") {
    return [
      {
        name: "Battery Level",
        key: "battery_level",
        type: "number",
        icon: <Battery60 fontSize="small" />,
        showBetweenOperator: false,
      },
    ];
  }
  return h;
});

const TripDetailScreen = ({ setShowMap, currentTrip, isLoaded }) => {
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [snappedCoordinates, setSnappedCoordinates] = useState([]);
  const [trackUserStatus, setTrackUserStatus] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loadingTripLogs, setLoadingTripLogs] = useState(true);
  const [loadingConnectivity, setLoadingConnectivity] = useState(true);
  const containerRef = useRef(null);
  const [tripDetails, setTripDetails] = useState([]);
  const [arrowMarkers, setArrowMarkers] = useState([]);

  const fetchTripPage = useCallback(
    async (page) => {
      try {
        const res = await fetch(
          `${domain}tracking/trip-logs/?trip_id=${currentTrip?.trip_id}&page=${page}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const data = await res.json();
        return data?.results || [];
      } catch (err) {
        console.error(`Error fetching page ${page}:`, err);
        return [];
      }
    },
    [currentTrip?.trip_id, domain, token],
  );

  const loadTripPages = useCallback(
    async (totalPages) => {
      setLoadingTripLogs(true);
      try {
        const pageResults = {};
        let currentPage = 1;

        const updatePoints = () => {
          const merged = [];
          for (let i = 1; i <= totalPages; i++) {
            if (pageResults[i]) merged.push(...pageResults[i]);
            else break;
          }
          setSnappedCoordinates(merged);
        };

        const runBatch = async () => {
          const batch = [];
          for (let i = 0; i < 5 && currentPage <= totalPages; i++) {
            const page = currentPage++;
            batch.push(
              fetchTripPage(page).then((points) => {
                pageResults[page] = points || [];
                updatePoints();
              }),
            );
          }
          if (batch.length > 0) {
            await Promise.all(batch);
            if (currentPage <= totalPages) await runBatch();
          }
        };

        await runBatch();
      } catch (error) {
        console.error("Error loading trip pages:", error);
        setSnappedCoordinates([]);
      } finally {
        setLoadingTripLogs(false);
      }
    },
    [fetchTripPage],
  );

  useEffect(() => {
    if (currentTrip?.trip_id) {
      const totalPages = currentTrip?.pagination_count || 1;
      loadTripPages(totalPages);
    } else {
      setSnappedCoordinates([]);
      setLoadingTripLogs(false);
    }
  }, [currentTrip, loadTripPages]);

  // Compute arrow markers using SVG
  useEffect(() => {
    if (!snappedCoordinates.length || !window.google?.maps?.geometry) return;

    const points = snappedCoordinates;
    const arrows = [];
    const MIN_DISTANCE = 1000; // meters

    const computeBearing = (from, to) => {
      return window.google.maps.geometry.spherical.computeHeading(
        new window.google.maps.LatLng(from.latitude, from.longitude),
        new window.google.maps.LatLng(to.latitude, to.longitude),
      );
    };

    const getDistance = (p1, p2) => {
      return window.google.maps.geometry.spherical.computeDistanceBetween(
        new window.google.maps.LatLng(p1.latitude, p1.longitude),
        new window.google.maps.LatLng(p2.latitude, p2.longitude),
      );
    };

    let lastArrowPoint = points[0];

    for (let i = 1; i < points.length; i++) {
      const current = points[i];
      const dist = getDistance(lastArrowPoint, current);

      if (dist >= MIN_DISTANCE) {
        const bearing = computeBearing(points[i - 1], current);

        const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24">
   
    <path 
      d="M12 2 L21 21 Q12 17 3 21 Z" 
      fill="#ffffff" 
      stroke="black" 
      stroke-width="1" 
      transform="rotate(${bearing}, 12, 12)" 
    />
  </svg>
`;

        const arrowIcon = {
          url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
          scaledSize: new window.google.maps.Size(15, 15),
          anchor: new window.google.maps.Point(8, 8),
        };

        arrows.push(
          <Marker
            key={`arrow-${arrows.length}`}
            position={{ lat: current.latitude, lng: current.longitude }}
            icon={arrowIcon}
          />,
        );

        lastArrowPoint = current;
      }
    }

    setArrowMarkers(arrows);
  }, [snappedCoordinates]);

  const getTrackUserStatus = useCallback(
    async (currentTrip) => {
      setLoadingConnectivity(true);
      try {
        const formattedDate = dayjs(currentTrip?.date, "DD-MM-YYYY").format(
          "YYYY-MM-DD",
        );
        const res = await fetch(
          `${domain}tracking/trip-device-status/?trip_id=${currentTrip?.trip_id}&date=${formattedDate}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const data = await res.json();
        setTrackUserStatus(data || []);
        return data;
      } catch (err) {
        console.error(`Error fetching connectivity data: `, err);
        setTrackUserStatus([]);
        return [];
      } finally {
        setLoadingConnectivity(false);
      }
    },
    [domain, token],
  );

  useEffect(() => {
    if (currentTrip?.trip_id) {
      getTrackUserStatus(currentTrip);
    } else {
      setTrackUserStatus([]);
      setLoadingConnectivity(false);
    }
  }, [currentTrip, getTrackUserStatus]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredPoints = snappedCoordinates?.filter((p) => p?.action_type);

  const deviceInfo = trackUserStatus?.find(
    (item) => item.device_model && item.device_platform && item.device_osverion,
  );

  const deviceModel = deviceInfo?.device_model || "N/A";
  const devicePlatform = deviceInfo?.device_platform || "N/A";
  const deviceOsVersion = deviceInfo?.device_osverion || "N/A";
  const deviceAppVersion = deviceInfo?.app_version || "N/A";

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setTripDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  // Wait for map to be loaded
  if (!isLoaded) return <Loader info="Loading map..." />;

  return (
    <>
      <Box
        sx={{
          bgcolor: "#fff",
          display: "flex",
          flexDirection: "column",
          p: { xs: 2, sm: 3 },
          gap: 4,
          backgroundColor: "#f5f5fd",
        }}
      >
        {/* Header and summary section (unchanged) */}
        <Box>
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => setShowMap(false)}
              sx={{ "&:hover": { bgcolor: "black.100" } }}
            >
              <ArrowBack sx={{ color: "black.700" }} />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "black.900", fontSize: 18 }}
            >
              Trip {currentTrip?.trip_id}
            </Typography>
            <Chip
              label={
                currentTrip?.stop_latitude ? "Completed" : "Trip Inprogress"
              }
              sx={{
                ml: "auto",
                bgcolor: "#d1fae5",
                color: "#065f46",
                fontWeight: 500,
                fontSize: 18,
              }}
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Card
                sx={{
                  border: "1px solid #e5e7eb",
                  boxShadow: "none",
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ p: 3, pb: 0 }}>
                  <Box
                    sx={{
                      top: 0,
                      display: "flex",
                      justifyContent: "space-between",
                      backgroundColor: "#fff",
                      borderBottom: "1px solid #e5e7eb",
                      pb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "black.900",
                      }}
                    >
                      {currentTrip?.user_name || "Trip Summary"}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "black.700",
                        fontWeight: 600,
                        fontSize: 16,
                      }}
                    >
                      {currentTrip?.date || "— —"}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          p: 2,
                          pb: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            sx={{ width: 40, height: 40, bgcolor: "#10b981" }}
                          >
                            <AccessTime sx={{ color: "white" }} />
                          </Avatar>
                          <Typography
                            variant="caption"
                            sx={{ color: "black.600", fontSize: 16 }}
                          >
                            Start Time
                          </Typography>
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, color: "black.900", pl: 6 }}
                        >
                          {currentTrip?.formatted_start_time || "--"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item md={2}></Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          p: 2,
                          pb: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            sx={{ width: 40, height: 40, bgcolor: "#ef4444" }}
                          >
                            <Flag sx={{ color: "white" }} />
                          </Avatar>
                          <Typography
                            variant="caption"
                            sx={{ color: "black.600", fontSize: 16 }}
                          >
                            End Time
                          </Typography>
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, color: "black.900", pl: 6 }}
                        >
                          {currentTrip?.formatted_end_time || "--"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item md={2}></Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          p: 2,
                          pb: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            sx={{ width: 40, height: 40, bgcolor: "#2563eb" }}
                          >
                            <AccessTime sx={{ color: "white" }} />
                          </Avatar>
                          <Typography
                            variant="caption"
                            sx={{ color: "black.600", fontSize: 16 }}
                          >
                            Duration
                          </Typography>
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, color: "black.900", pl: 6 }}
                        >
                          {currentTrip?.duration || "--"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item md={2}></Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          p: 2,
                          pb: 0,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            sx={{ width: 40, height: 40, bgcolor: "#db2777" }}
                          >
                            <LocationOn sx={{ color: "white" }} />
                          </Avatar>
                          <Typography
                            variant="caption"
                            sx={{ color: "black.600", fontSize: 16 }}
                          >
                            Distance Covered
                          </Typography>
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, color: "black.900", pl: 6 }}
                        >
                          {currentTrip?.distance_cover || "0"} km
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card
                sx={{
                  border: "1px solid #e5e7eb",
                  boxShadow: "none",
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                    Meta Data
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {[
                      ["Trip ID", currentTrip?.raw_trip_id],
                      ["Device", deviceModel],
                      ["Platform", devicePlatform],
                      ["OS Version", deviceOsVersion],
                      ["App Version", deviceAppVersion],
                    ].map(([label, value]) => (
                      <Box
                        key={label}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "black.900", fontSize: 16 }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "black.600",
                            fontSize: 16,
                          }}
                        >
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Grid
            container
            spacing={3}
            sx={{
              maxHeight: "50%",
              alignItems: "stretch",
            }}
          >
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                width: "100%",
                maxHeight: { sm: 700, md: 700, lg: 700, xl: 750 },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Card
                sx={{
                  flexGrow: 1,
                  border: "1px solid #e5e7eb",
                  boxShadow: "none",
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "#fff",
                      borderBottom: "1px solid #e5e7eb",
                      p: { xs: 2, sm: 3 },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "black.900", mb: 0.5 }}
                    >
                      Trip details
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "black.500", fontSize: 15 }}
                    >
                      {currentTrip?.date} at {currentTrip?.formatted_start_time}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flexGrow: 1,
                      p: { xs: 2, sm: 3 },
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        p: 2,
                        bgcolor: "white",
                        borderRadius: 3,
                        border: "1px solid #a7f3d0",
                        alignItems: "flex-start",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: "#10b981",
                          flexShrink: 0,
                        }}
                      >
                        <Flag sx={{ color: "white" }} />
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: "black.900",
                            mb: 0.5,
                            fontSize: 18,
                          }}
                        >
                          Trip Started
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "black", lineHeight: 1.6, fontSize: 16 }}
                        >
                          {currentTrip?.start_location_address ||
                            "Address not found!"}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "black.900",
                          flexShrink: 0,
                          fontSize: 16,
                        }}
                      >
                        {currentTrip?.formatted_start_time}
                      </Typography>
                    </Box>

                    {filteredPoints
                      ?.filter((p) => p.action_type === "Checkpoint")
                      ?.map((checkpoint, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            gap: 2,
                            p: 2,
                            bgcolor: "white",
                            borderRadius: 3,
                            border: "1px solid #fdba74",
                            alignItems: "flex-start",
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: "#f97316",
                              flexShrink: 0,
                            }}
                          >
                            <LocationOn sx={{ color: "white" }} />
                          </Avatar>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: "black.900",
                                mb: 0.5,
                                fontSize: 18,
                              }}
                            >
                              {checkpoint?.notes ||
                                checkpoint?.checkpoint_type ||
                                "Auto Check point"}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "black",
                                lineHeight: 1.6,
                                fontSize: 16,
                              }}
                            >
                              {checkpoint?.address || "Address not found!"}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "black.900",
                              flexShrink: 0,
                              fontSize: 16,
                            }}
                          >
                            {checkpoint?.time}
                          </Typography>
                        </Box>
                      ))}

                    {currentTrip?.stop_latitude && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          p: 2,
                          bgcolor: "white",
                          borderRadius: 3,
                          border: "1px solid #fca5a5",
                          alignItems: "flex-start",
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: "#ef4444",
                            flexShrink: 0,
                          }}
                        >
                          <Flag sx={{ color: "white" }} />
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: "black.900",
                              mb: 0.5,
                              fontSize: 18,
                            }}
                          >
                            Trip Ended
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "black",
                              lineHeight: 1.6,
                              fontSize: 16,
                            }}
                          >
                            {currentTrip?.stop_location_address ||
                              "Address not found"}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "black.900",
                            flexShrink: 0,
                            fontSize: 16,
                          }}
                        >
                          {currentTrip?.formatted_end_time}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
              sx={{
                width: "100%",
                maxHeight: { sm: 700, md: 700, lg: 700, xl: 750 },
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              <Card
                sx={{
                  border: "1px solid #e5e7eb",
                  boxShadow: "none",
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "black.900", mb: 2 }}
                  >
                    View Trip
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      height: { xs: 240, sm: 300, md: 340 },
                      bgcolor: "black.100",
                      borderRadius: 3,
                      overflow: "hidden",
                    }}
                  >
                    <GoogleMap
                      mapContainerStyle={{
                        width: "100%",
                        height: "100%",
                        borderRadius: 12,
                      }}
                      center={{
                        lat: currentTrip?.start_latitude || 0,
                        lng: currentTrip?.start_longitude || 0,
                      }}
                      zoom={13}
                    >
                      {currentTrip?.start_latitude && (
                        <Marker
                          position={{
                            lat: currentTrip.start_latitude,
                            lng: currentTrip.start_longitude,
                          }}
                          title="Starting Point"
                          icon={{
                            url:
                              "data:image/svg+xml;charset=UTF-8," +
                              encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="60" viewBox="0 0 40 60">
                    <path d="M20 2
                             C11 2, 4 9, 4 18
                             C4 32, 20 58, 20 58
                             C20 58, 36 32, 36 18
                             C36 9, 29 2, 20 2Z"
                          fill="#10B981"
                          stroke="rgba(0,0,0,0.25)"
                          stroke-width="2"/>
                    <circle cx="20" cy="20" r="9" fill="white"/>
                    <path d="M16 15v10h2v-4h8l-1.5-3L26 15H16z" fill="#10B981"/>
                  </svg>
                `),
                            scaledSize: new window.google.maps.Size(30, 45),
                            anchor: new window.google.maps.Point(15, 45),
                          }}
                        />
                      )}

                      {filteredPoints
                        ?.filter((p) => p.action_type === "Checkpoint")
                        ?.map((checkpoint, index) => (
                          <Marker
                            key={index}
                            position={{
                              lat: checkpoint.latitude,
                              lng: checkpoint.longitude,
                            }}
                            title={`${
                              checkpoint?.notes
                                ? checkpoint?.notes
                                : checkpoint?.checkpoint_type
                            }`}
                            icon={{
                              url:
                                "data:image/svg+xml;charset=UTF-8," +
                                encodeURIComponent(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="60" viewBox="0 0 40 60">
                      <path d="M20 2
                               C11 2, 4 9, 4 18
                               C4 32, 20 58, 20 58
                               C20 58, 36 32, 36 18
                               C36 9, 29 2, 20 2Z"
                            fill="#FF9800"
                            stroke="rgba(0,0,0,0.25)"
                            stroke-width="2"/>
                      <circle cx="20" cy="20" r="9" fill="white"/>
                      <path d="M16 15v10h2v-4h8l-1.5-3L26 15H16z" fill="#FF9800"/>
                    </svg>
                  `),
                              scaledSize: new window.google.maps.Size(30, 45),
                              anchor: new window.google.maps.Point(15, 45),
                            }}
                          />
                        ))}

                      {currentTrip?.stop_latitude && (
                        <Marker
                          position={{
                            lat: currentTrip.stop_latitude,
                            lng: currentTrip.stop_longitude,
                          }}
                          title="Ending Point"
                          icon={{
                            url:
                              "data:image/svg+xml;charset=UTF-8," +
                              encodeURIComponent(`
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="60" viewBox="0 0 40 60">
                    <path d="M20 2
                             C11 2, 4 9, 4 18
                             C4 32, 20 58, 20 58
                             C20 58, 36 32, 36 18
                             C36 9, 29 2, 20 2Z"
                          fill="#EF4444"
                          stroke="rgba(0,0,0,0.25)"
                          stroke-width="2"/>
                    <circle cx="20" cy="20" r="9" fill="white"/>
                    <path d="M16 15v10h2v-4h8l-1.5-3L26 15H16z" fill="#EF4444"/>
                  </svg>
                `),
                            scaledSize: new window.google.maps.Size(30, 45),
                            anchor: new window.google.maps.Point(15, 45),
                          }}
                        />
                      )}

                      <Polyline
                        path={snappedCoordinates.map((coord) => ({
                          lat: coord.latitude,
                          lng: coord.longitude,
                        }))}
                        options={{
                          strokeColor: "#2196F3",
                          strokeOpacity: 0.85,
                          strokeWeight: 8,
                        }}
                      />

                      {/* Direction arrows using SVG */}
                      {arrowMarkers}
                    </GoogleMap>
                  </Box>
                </CardContent>
              </Card>

              <Card
                sx={{
                  flexGrow: 1,
                  border: "1px solid #e5e7eb",
                  boxShadow: "none",
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 2, sm: 3 },
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    bgcolor: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                      backgroundColor: "#fff",
                      pb: 1.5,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "black.900",
                        mb: 1,
                      }}
                    >
                      Connectivity Logs
                    </Typography>
                  </Box>

                  <TableContainer
                    sx={{
                      flexGrow: 1,
                      overflowY: "auto",
                      borderRadius: 2,
                    }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {[
                            "Date",
                            "Time",
                            "Platform",
                            "OS",
                            "GPS",
                            "Network",
                          ].map((head) => (
                            <TableCell
                              key={head}
                              sx={{
                                py: 1.5,
                                px: 1.5,
                                fontWeight: 600,
                                color: "black.700",
                                backgroundColor: "#fff",
                                fontSize: 16,
                              }}
                            >
                              {head}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {loadingConnectivity ? (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              sx={{
                                textAlign: "center",
                                py: 6,
                                color: "black.500",
                                fontSize: 16,
                              }}
                            >
                              <Loader info="Loading connectivity logs..." />
                            </TableCell>
                          </TableRow>
                        ) : !trackUserStatus || trackUserStatus.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              sx={{
                                textAlign: "center",
                                py: 6,
                                color: "black.500",
                                fontSize: 16,
                              }}
                            >
                              No connectivity logs available
                            </TableCell>
                          </TableRow>
                        ) : (
                          trackUserStatus.map((item, index) => (
                            <TableRow
                              key={index}
                              sx={{ borderBottom: "1px solid #f3f4f6" }}
                            >
                              <TableCell sx={{ fontSize: 16, color: "black" }}>
                                {item?.date || "N/A"}
                              </TableCell>
                              <TableCell sx={{ fontSize: 16, color: "black" }}>
                                {item?.time_format || "N/A"}
                              </TableCell>
                              <TableCell sx={{ fontSize: 16, color: "black" }}>
                                {item?.device_platform || "N/A"}
                              </TableCell>
                              <TableCell sx={{ fontSize: 16, color: "black" }}>
                                {item?.device_osverion || "N/A"}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    item?.provider_gps === true ||
                                    item?.provider_gps === null
                                      ? "ON"
                                      : item?.provider_gps === false
                                        ? "OFF"
                                        : "N/A"
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor:
                                      item?.provider_gps === true ||
                                      item?.provider_gps === null
                                        ? "#d1fae5"
                                        : item?.provider_gps === false
                                          ? "#fecaca"
                                          : "transparent",
                                    color:
                                      item?.provider_gps === true ||
                                      item?.provider_gps === null
                                        ? "#065f46"
                                        : item?.provider_gps === false
                                          ? "#991b1b"
                                          : "black",
                                    fontSize: 16,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    item?.connectivity_network
                                      ? "Online"
                                      : "Offline"
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor: item?.connectivity_network
                                      ? "#d1fae5"
                                      : "#f3f4f6",
                                    color: item?.connectivity_network
                                      ? "#065f46"
                                      : "black",
                                    fontSize: 16,
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Paper sx={{ width: "100%", height: "78vh" }}>
          {!loadingTripLogs && (
            <CustomFilter
              title="Trip Logs"
              filterHeaders={filteredTripHeader}
              data={snappedCoordinates}
              filteredData={tripDetails}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={true}
            />
          )}

          <Mui.Grid sx={{ ml: 2, mr: 2 }}>
            <Mui.TableContainer ref={containerRef} sx={{ maxHeight: 560 }}>
              <Mui.Table className="border">
                <Mui.TableHead
                  sx={{
                    backgroundColor: themes.primary,
                    position: "sticky",
                    top: -1,
                    zIndex: 10,
                  }}
                >
                  <Mui.TableRow sx={tableRowSize}>
                    {tripHeader?.map((value) => (
                      <Mui.TableCell
                        key={value.key}
                        sx={{
                          color: themes.headLine,
                          fontWeight: "bold",
                        }}
                      >
                        {value?.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {loadingTripLogs ? (
                    <Mui.TableRow>
                      <Mui.TableCell
                        colSpan={tripHeader.length}
                        align="center"
                        sx={{ py: 3 }}
                      >
                        <Loader />
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : tripDetails?.length === 0 ? (
                    <InfoMessage dataLength={snappedCoordinates} />
                  ) : (
                    tripDetails
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((value, rowIndex) => {
                        const batteryLevel = value?.battery_level ?? 0;
                        const batteryColor =
                          batteryLevel >= 70
                            ? "#4caf50"
                            : batteryLevel >= 30
                              ? "#ff9800"
                              : "#f44336";

                        return (
                          <Mui.TableRow key={rowIndex} sx={tableRowSize}>
                            {tripHeader.map((head) => (
                              <Mui.TableCell key={head.key}>
                                {head.key === "latLong" ? (
                                  value.latitude && value.longitude ? (
                                    `${value.latitude} - ${value.longitude}`
                                  ) : (
                                    "N/A"
                                  )
                                ) : head.key === "batteryPercentage" ? (
                                  <>
                                    {value?.battery_charging ? (
                                      <BatteryChargingFullRounded
                                        sx={{
                                          fontSize: 16,
                                          color: batteryColor,
                                          marginRight: "8px",
                                        }}
                                      />
                                    ) : (
                                      <BatteryFullRounded
                                        sx={{
                                          fontSize: 16,
                                          color: batteryColor,
                                          marginRight: "8px",
                                        }}
                                      />
                                    )}
                                    {value?.battery_level ?? "N/A"}%
                                  </>
                                ) : (
                                  (value[head.key] ?? "N/A")
                                )}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                        );
                      })
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            {!loadingTripLogs && (
              <Box sx={{ mt: 1 }}>
                {tripDetails?.length > 25 && (
                  <Mui.TablePagination
                    className="custom-pagination"
                    component="div"
                    rowsPerPageOptions={[25, 50, 75, 100]}
                    count={tripDetails?.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                )}
              </Box>
            )}
          </Mui.Grid>
        </Paper>
      </Box>
    </>
  );
};

export default TripDetailScreen;
